import type { ProgressMark, ProgressStore, StationProgress } from './types'

/** One store per Firebase uid; progress is intentionally local-only in V1. */
export const PROGRESS_STORAGE_PREFIX = 'ospe-progress:'
/** Same-tab updates do not emit the browser `storage` event, so we emit this too. */
export const PROGRESS_STORAGE_EVENT = 'ospe-progress-store:changed'

interface ProgressDetail {
  uid: string
}

const EMPTY_PROGRESS: StationProgress = {
  mark: 'unseen',
  bookmarked: false,
}

function normalizeUid(uid: string | null | undefined): string {
  return typeof uid === 'string' ? uid.trim() : ''
}

function normalizeStationId(stationId: string | null | undefined): string {
  return typeof stationId === 'string' ? stationId.trim() : ''
}

function getStorageKey(uid: string): string {
  return `${PROGRESS_STORAGE_PREFIX}${normalizeUid(uid)}`
}

function defaultProgressStore(): ProgressStore {
  return {
    version: 1,
    stations: {},
    updatedAt: new Date().toISOString(),
  }
}

function cloneStationProgress(progress: StationProgress): StationProgress {
  return {
    mark: progress.mark,
    bookmarked: progress.bookmarked,
    ...(progress.answeredAt ? { answeredAt: progress.answeredAt } : {}),
    ...(progress.revealedQuestionIds?.length
      ? { revealedQuestionIds: [...progress.revealedQuestionIds] }
      : {}),
  }
}

function cloneStore(store: ProgressStore): ProgressStore {
  const stations: Record<string, StationProgress> = {}
  for (const [stationId, progress] of Object.entries(store.stations)) {
    const normalizedId = normalizeStationId(stationId)
    if (normalizedId) stations[normalizedId] = cloneStationProgress(progress)
  }
  return {
    version: 1,
    updatedAt: store.updatedAt,
    stations,
  }
}

function normalizeMark(value: unknown): ProgressMark {
  return value === 'known' || value === 'review' || value === 'unseen' ? value : 'unseen'
}

function normalizeStationProgress(value: unknown): StationProgress {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return cloneStationProgress(EMPTY_PROGRESS)

  const source = value as Record<string, unknown>
  const answeredAt = typeof source.answeredAt === 'string' && source.answeredAt.trim()
    ? source.answeredAt
    : undefined
  const revealedQuestionIds = Array.isArray(source.revealedQuestionIds)
    ? Array.from(new Set(
        source.revealedQuestionIds
          .filter((id): id is string => typeof id === 'string')
          .map((id) => id.trim())
          .filter(Boolean),
      ))
    : undefined

  return {
    mark: normalizeMark(source.mark),
    bookmarked: source.bookmarked === true,
    ...(answeredAt ? { answeredAt } : {}),
    ...(revealedQuestionIds?.length ? { revealedQuestionIds } : {}),
  }
}

function parseProgressStore(payload: unknown): ProgressStore {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return defaultProgressStore()

  const source = payload as Record<string, unknown>
  // Keep version one tolerant of stores created by early builds that omitted
  // the envelope version, but never trust malformed station values.
  if (source.version !== undefined && source.version !== 1) return defaultProgressStore()
  if (!source.stations || typeof source.stations !== 'object' || Array.isArray(source.stations)) return defaultProgressStore()

  const stations: Record<string, StationProgress> = {}
  for (const [stationId, stationData] of Object.entries(source.stations as Record<string, unknown>)) {
    const normalizedId = normalizeStationId(stationId)
    if (normalizedId) stations[normalizedId] = normalizeStationProgress(stationData)
  }

  const updatedAt = typeof source.updatedAt === 'string' && source.updatedAt.trim()
    ? source.updatedAt
    : new Date().toISOString()
  return { version: 1, stations, updatedAt }
}

function readStore(uid: string): ProgressStore {
  const normalizedUid = normalizeUid(uid)
  if (!normalizedUid || typeof window === 'undefined') return defaultProgressStore()

  try {
    const raw = window.localStorage.getItem(getStorageKey(normalizedUid))
    return raw ? parseProgressStore(JSON.parse(raw) as unknown) : defaultProgressStore()
  } catch {
    // localStorage can be unavailable in private mode, blocked contexts, or
    // after quota/security changes. Study actions should remain usable.
    return defaultProgressStore()
  }
}

function dispatchProgressChange(uid: string): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return
  try {
    window.dispatchEvent(new CustomEvent<ProgressDetail>(PROGRESS_STORAGE_EVENT, { detail: { uid } }))
  } catch {
    // Custom events are only a UI notification; persistence already succeeded.
  }
}

function writeStore(uid: string, store: ProgressStore): ProgressStore {
  const normalizedUid = normalizeUid(uid)
  const snapshot = cloneStore(store)
  snapshot.updatedAt = new Date().toISOString()
  if (!normalizedUid || typeof window === 'undefined') return snapshot

  let persisted = false
  try {
    window.localStorage.setItem(getStorageKey(normalizedUid), JSON.stringify(snapshot))
    persisted = true
  } catch {
    // Return the in-memory snapshot so the caller updates immediately even if
    // this browser cannot write localStorage.
  }
  if (persisted) dispatchProgressChange(normalizedUid)
  return snapshot
}

function emptyStationProgress(): StationProgress {
  return cloneStationProgress(EMPTY_PROGRESS)
}

function updateProgress(
  uid: string,
  stationId: string,
  updater: (progress: StationProgress) => StationProgress,
): StationProgress {
  const normalizedUid = normalizeUid(uid)
  const normalizedStationId = normalizeStationId(stationId)
  if (!normalizedUid || !normalizedStationId) return emptyStationProgress()

  const store = readStore(normalizedUid)
  const current = normalizeStationProgress(store.stations[normalizedStationId])
  const nextStationProgress = normalizeStationProgress(updater(cloneStationProgress(current)))
  const saved = writeStore(normalizedUid, {
    ...store,
    stations: {
      ...store.stations,
      [normalizedStationId]: nextStationProgress,
    },
  })
  return cloneStationProgress(saved.stations[normalizedStationId] ?? emptyStationProgress())
}

export interface ProgressSummary {
  total: number
  known: number
  review: number
  unseen: number
  bookmarked: number
}

export function getProgressStore(uid: string): ProgressStore {
  return cloneStore(readStore(uid))
}

export function getStationProgress(uid: string, stationId: string): StationProgress {
  const normalizedStationId = normalizeStationId(stationId)
  if (!normalizedStationId) return emptyStationProgress()
  const progress = readStore(uid).stations[normalizedStationId]
  return progress ? cloneStationProgress(progress) : emptyStationProgress()
}

export function setStationMark(uid: string, stationId: string, mark: ProgressMark): StationProgress {
  const safeMark: ProgressMark = normalizeMark(mark)
  return updateProgress(uid, stationId, (current) => {
    const nextMark = current.mark === safeMark ? 'unseen' : safeMark
    return {
      ...current,
      mark: nextMark,
      answeredAt: nextMark === 'unseen' ? current.answeredAt : current.answeredAt ?? new Date().toISOString(),
    }
  })
}

/** Records the latest self-assessment without toggle semantics. A later correct
 * answer intentionally resolves a station that was previously in review. */
export function recordStationResult(uid: string, stationId: string, result: 'correct' | 'wrong'): StationProgress {
  return updateProgress(uid, stationId, (current) => ({
    ...current,
    mark: result === 'correct' ? 'known' : 'review',
    answeredAt: new Date().toISOString(),
  }))
}

export function toggleStationBookmark(uid: string, stationId: string): StationProgress {
  return updateProgress(uid, stationId, (current) => ({
    ...current,
    bookmarked: !current.bookmarked,
  }))
}

export function setStationRevealedQuestionIds(uid: string, stationId: string, questionIds: string[]): StationProgress {
  const normalizedIds = Array.from(new Set(
    (Array.isArray(questionIds) ? questionIds : [])
      .filter((id): id is string => typeof id === 'string')
      .map((id) => id.trim())
      .filter(Boolean),
  ))
  return updateProgress(uid, stationId, (current) => ({
    ...current,
    revealedQuestionIds: normalizedIds,
    answeredAt: normalizedIds.length > 0 ? current.answeredAt ?? new Date().toISOString() : current.answeredAt,
  }))
}

export function toggleStationQuestionReveal(uid: string, stationId: string, questionId: string): StationProgress {
  const normalizedQuestionId = normalizeStationId(questionId)
  if (!normalizedQuestionId) return getStationProgress(uid, stationId)
  return updateProgress(uid, stationId, (current) => {
    const nextIds = new Set(current.revealedQuestionIds ?? [])
    if (nextIds.has(normalizedQuestionId)) nextIds.delete(normalizedQuestionId)
    else nextIds.add(normalizedQuestionId)
    const revealedQuestionIds = Array.from(nextIds)
    return {
      ...current,
      revealedQuestionIds,
      answeredAt: revealedQuestionIds.length > 0 ? current.answeredAt ?? new Date().toISOString() : current.answeredAt,
    }
  })
}

export function getProgressSummary(uid: string, stationIds: string[]): ProgressSummary {
  const normalizedIds = Array.from(new Set((stationIds ?? []).map(normalizeStationId).filter(Boolean)))
  const store = readStore(uid)
  const summary: ProgressSummary = {
    total: normalizedIds.length,
    known: 0,
    review: 0,
    unseen: 0,
    bookmarked: 0,
  }

  for (const stationId of normalizedIds) {
    const progress = store.stations[stationId]
    if (!progress) {
      summary.unseen += 1
      continue
    }
    if (progress.bookmarked) summary.bookmarked += 1
    if (progress.mark === 'known') summary.known += 1
    else if (progress.mark === 'review') summary.review += 1
    else summary.unseen += 1
  }
  return summary
}

export function clearStationProgress(uid: string, stationId: string): ProgressStore {
  const normalizedUid = normalizeUid(uid)
  const normalizedStationId = normalizeStationId(stationId)
  if (!normalizedUid || !normalizedStationId) return getProgressStore(normalizedUid)

  const store = readStore(normalizedUid)
  if (!store.stations[normalizedStationId]) return cloneStore(store)
  const { [normalizedStationId]: _removed, ...remaining } = store.stations
  return writeStore(normalizedUid, { ...store, stations: remaining })
}
